import { Component, Inject, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { CancellationReason, Offer, Reservation, Slot } from '../../../reservation.model';

import { DateService } from '../../../../common/date.service';
import { InfoboxService } from '../../../../common/infobox/infobox.service';
import { AuthService } from '../../../../auth/auth.service';
import { ReservationSummaryApiService } from '../../services/reservation-summary-api.service';
import { Status } from '../../../../common/enums/Status';

import { LoaderService } from '../../../../common/loader/loader.service';
import { ReservationSummaryStoreService } from '../../services/reservation-summary-store.service';
import { ReservationSummaryCommonService } from '../../services/reservation-summary-common.service';
import { ReservationService } from '../../../reservation.service';
import { toFixedInt } from '@/_utils/to-fixed-int';
import { Permissions } from '@/common/enums/Permissions';

interface DialogData {
	reservationId: number;
	offerId: number;
	slotId: number;
}

@Component({
	selector: 'app-reservation-summary-cancel-dialog',
	templateUrl: './reservation-summary-cancel-dialog.component.html',
	styleUrls: ['./reservation-summary-cancel-dialog.component.scss'],
})
export class ReservationSummaryCancelDialogComponent implements OnInit {
	isCancelledByOskar = false;
	reservation: Reservation;
	cancellationReasons: Observable<CancellationReason[]>;
	todayDate = '';

	cancellationCost = 0;
	daysBeforeDeparture = 0;
	cancellationPercentage = 0;
	cancellationReasonId: string;

	frontendCancellationPrice = '';

	readonly Permissions = Permissions;

	constructor(
		@Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
		public dialogRef: MatDialogRef<ReservationSummaryCancelDialogComponent>,
		public dialog: MatDialog,
		private readonly store: ReservationSummaryStoreService,
		private readonly dateService: DateService,
		public authService: AuthService,
		private readonly apiService: ReservationSummaryApiService,
		private readonly commonService: ReservationSummaryCommonService,
		private readonly infobox: InfoboxService,
		private readonly service: ReservationService,
		private readonly loader: LoaderService,
	) {}

	ngOnInit(): void {
		const { cancellationReasons } = this.store.getters();
		this.todayDate = this.dateService.now().date;

		this.reservation = this.service.reservation$.getValue();
		this.checkForAlerts();

		const { reservationId, slotId, offerId } = this.dialogData;
		if (reservationId) {
			this.cancellationCost = this.reservation.cancellationCost;

			if (offerId) {
				const offer = this.reservation.offer.find((v) => v.id === offerId);

				if (!offer) return;

				this.cancellationCost = offer.cancellationCost;
				this.daysBeforeDeparture = offer.daysBeforeDeparture;
				this.cancellationPercentage = offer.cancellationPercentage;

				if (slotId) {
					const offer = this.reservation.offer.find((v) => v.slot.find((slot) => slot.id === slotId));

					if (!offer) return;

					this.cancellationCost = offer.slot.find((v) => v.id === slotId).cancellationCost;
				}
			}
		}

		this.cancellationReasons = cancellationReasons();
	}

	checkForAlerts() {
		this.isCancelledByOskar = this.reservation?.alerts.some((v) => v.active && v.id === 'COO');
	}

	closeDialog() {
		this.dialogRef.close();
	}

	getFrontendStatus(withoutCost: boolean, status: Status) {
		if (withoutCost) {
			return status === Status.OPTION ? Status.REJECTED_SELLER : Status.CANCELED_WITHOUT_COST;
		} else {
			return status === Status.OPTION ? Status.REJECTED_CLIENT : Status.CANCELED_BY_CLIENT;
		}
	}

	closeAllDialogs() {
		this.dialog.closeAll();
	}

	get isSlot(): boolean {
		return !!this.dialogData.slotId && !!this.dialogData.offerId && !!this.dialogData.reservationId;
	}

	get isOffer(): boolean {
		return !!!this.dialogData.slotId && !!this.dialogData.offerId && !!this.dialogData.reservationId;
	}

	get offer(): Offer {
		return this.reservation.offer.find((v) => v.id === this.dialogData.offerId);
	}

	get slot(): Slot {
		return this.offer?.slot.find((v) => v.id === this.dialogData.slotId);
	}

	get isReservation(): boolean {
		return !!!this.dialogData.slotId && !!!this.dialogData.offerId && !!this.dialogData.reservationId;
	}

	cancel(withoutCost = false) {
		this.loader.show();
		const { reservationId, slotId, offerId } = this.dialogData;
		const { getReservation, cancelReservation, cancelReservationSlot, cancelReservationOffer } =
			this.apiService.actions();

		let offerPayload: any = {};
		let slotPayload: any = {};

		const cancelAction = async (
			actionMethod: (id: number, payload: any) => Promise<boolean>,
			payload,
			id: number,
		) => {
			if (!id) return this.loader.hide();
			const success = await actionMethod(id, payload);
			if (success) {
				getReservation(reservationId);
				this.infobox.success('Anulowano pomyślnie');
				this.closeAllDialogs();
				this.loader.hide();
			}
		};
		const reservationPayload = {
			frontendStatus: this.getFrontendStatus(withoutCost, this.reservation.status),
			frontendCancellationPrice: withoutCost ? null : this.formatCancellationPrice(),
			reservationCancellationReason: !this.isCancelledByOskar
				? [
						{
							cancellationReason: this.cancellationReasonId,
							reservation: this.reservation.id,
							reservationOffer: null,
							reservationOfferSlot: null,
						},
					]
				: null,
		};

		if (this.offer) {
			offerPayload = {
				frontendCancellationPrice: withoutCost ? null : this.formatCancellationPrice(),
				frontendStatus: this.getFrontendStatus(withoutCost, this.offer.status as Status),
				offerCancellationReason: !this.isCancelledByOskar
					? [
							{
								cancellationReason: this.cancellationReasonId,
								reservation: null,
								reservationOffer: offerId,
								reservationOfferSlot: null,
							},
						]
					: null,
			};
		}

		if (this.slot) {
			slotPayload = {
				frontendStatus: this.getFrontendStatus(withoutCost, this.slot.status as Status),

				frontendCancellationPrice: withoutCost ? null : this.formatCancellationPrice(),
				slotCancellationReason: !this.isCancelledByOskar
					? [
							{
								cancellationReason: this.cancellationReasonId,
								reservation: null,
								reservationOffer: null,
								reservationOfferSlot: slotId,
							},
						]
					: null,
			};
		}

		if (reservationId && !offerId && !slotId) {
			if (this.frontendCancellationPrice === '') delete reservationPayload.frontendCancellationPrice;
			cancelAction(cancelReservation, reservationPayload, reservationId);
		} else if (reservationId && offerId && !slotId) {
			if (this.frontendCancellationPrice === '') delete offerPayload.frontendCancellationPrice;
			cancelAction(cancelReservationOffer, offerPayload, offerId);
		} else if (reservationId && offerId && slotId) {
			if (this.frontendCancellationPrice === '') delete slotPayload.frontendCancellationPrice;
			cancelAction(cancelReservationSlot, slotPayload, slotId);
		}
	}

	get cancelButtonText() {
		const { reservationId, slotId, offerId } = this.dialogData;

		if (reservationId && !offerId && !slotId) return 'Anuluj rezerwację';
		else if (reservationId && offerId && !slotId) return 'Anuluj ofertę';
		else if (reservationId && offerId && slotId) return 'Anuluj uczestnika';
		else return 'Anuluj';
	}

	protected readonly parseInt = parseInt;

	formatCancellationPrice() {
		const value = +this.frontendCancellationPrice;
		return value ? toFixedInt(value * 100, 0) : 0;
	}
}
